import { useRef, useState } from 'react';
import SlideUpTransition from '../../../components/SlideUpTransition';
import {
    Alert,
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { AudioFile, PhotoLibrary, VideoLibrary } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useApi } from '../../../api/Api';
import { RequestSnackbar, useRequest } from '../../../api/Request';
import { CommunityPost, PostCategory } from '../../../database/community';
import { getConfig } from '../../../config';
import MentionInput, { MentionInputHandle } from '../../../components/MentionInput';
import AudioPlayer from '../audio/AudioPlayer';
import VideoPlayer from '../video/VideoPlayer';

interface PostEditDialogProps {
    post: CommunityPost;
    open: boolean;
    onClose: () => void;
    onSave: (post: CommunityPost) => void;
}

const PostEditDialog: React.FC<PostEditDialogProps> = ({ post, open, onClose, onSave }) => {
    const api = useApi();
    const theme = useTheme();
    const communityImagesBucket = getConfig().media.communityImagesBucket;
    const updateRequest = useRequest();

    const [content, setContent] = useState(post.content);
    const mentionInputRef = useRef<MentionInputHandle>(null);

    // Track existing media state
    const [existingMediaRemoved, setExistingMediaRemoved] = useState(false);

    // Track new file to upload
    const [fileToUpload, setFileToUpload] = useState<File>();
    const [newPreviewUrl, setNewPreviewUrl] = useState<string>();
    const [fileError, setFileError] = useState('');

    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const existingMediaUrl = `${communityImagesBucket}/${post.owner}/${post.createdAt}`;
    const showExistingMedia = post.containsMedia && !existingMediaRemoved && !fileToUpload;

    const getFileAccept = () => {
        switch (post.category) {
            case PostCategory.MUSIC:
                return '.mp3,.wav,.aac,.m4a,.ogg,.flac,audio/mpeg,audio/wav,audio/aac,audio/mp4,audio/ogg,audio/flac';
            case PostCategory.VIDEO:
                return '.mp4,.webm,.ogg,.mov,.avi,.mkv,video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/x-matroska';
            case PostCategory.PHOTO:
                return '.jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp';
            case PostCategory.COMMUNITY:
                return '.jpg,.jpeg,.png,image/jpeg,image/png';
        }
    };

    const getUploadIcon = () => {
        switch (post.category) {
            case PostCategory.MUSIC:
                return <AudioFile />;
            case PostCategory.VIDEO:
                return <VideoLibrary />;
            default:
                return <PhotoLibrary />;
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setFileError('');
        if (newPreviewUrl) URL.revokeObjectURL(newPreviewUrl);
        setFileToUpload(file);
        setNewPreviewUrl(URL.createObjectURL(file));
        setExistingMediaRemoved(false);
        event.target.value = '';
    };

    const handleRemoveNewFile = () => {
        if (newPreviewUrl) URL.revokeObjectURL(newPreviewUrl);
        setFileToUpload(undefined);
        setNewPreviewUrl(undefined);
    };

    const handleRemoveExistingMedia = () => {
        setExistingMediaRemoved(true);
    };

    const handleSubmit = () => {
        const serialized = mentionInputRef.current?.getSerializedValue() ?? content;

        const containsMedia = fileToUpload !== undefined || (post.containsMedia && !existingMediaRemoved);
        const removeMedia = post.containsMedia && existingMediaRemoved && !fileToUpload;

        updateRequest.onStart();
        api.updateCommunityPost(post.owner, post.createdAt, {
            content: serialized,
            containsMedia,
            removeMedia,
        })
            .then((response) => {
                const updatedPost = response.data;

                if (fileToUpload) {
                    setIsUploadingMedia(true);
                    api.communityPostGeneratePresignedUrl(updatedPost, fileToUpload.type)
                        .then((resp) => {
                            api.uploadMediaToS3({
                                presignedUrl: resp.data.presignedUrl,
                                mediaContent: fileToUpload,
                                contentType: fileToUpload.type,
                                onUploadProgress: (progressEvent) => {
                                    const pct = Math.round(
                                        (progressEvent.loaded * 100) /
                                            (progressEvent.total || 1),
                                    );
                                    setUploadProgress(pct);
                                },
                            })
                                .then(() => {
                                    setIsUploadingMedia(false);
                                    setUploadProgress(0);
                                    updateRequest.onSuccess('Post updated');
                                    onSave(updatedPost);
                                    onClose();
                                })
                                .catch((err: unknown) => {
                                    console.error('Failed to upload new media:', err);
                                    setIsUploadingMedia(false);
                                    setUploadProgress(0);
                                    updateRequest.onFailure();
                                });
                        })
                        .catch((err: unknown) => {
                            console.error('Failed to generate presigned URL:', err);
                            setIsUploadingMedia(false);
                            updateRequest.onFailure();
                        });
                } else {
                    updateRequest.onSuccess('Post updated');
                    onSave(updatedPost);
                    onClose();
                }
            })
            .catch((err) => {
                console.error('Update community post:', err);
                updateRequest.onFailure(err);
            });
    };

    return (
        <Dialog
            open={open}
            onClose={updateRequest.isLoading() ? undefined : onClose}
            fullWidth
            maxWidth='sm'
            TransitionComponent={SlideUpTransition}
        >
            <RequestSnackbar request={updateRequest} />
            <DialogTitle
                sx={{ color: theme.palette.secondary.main }}
            >
                Edit Post
                <IconButton
                    onClick={onClose}
                    disabled={updateRequest.isLoading()}
                    sx={{
                        position: 'absolute',
                        right: 10,
                        top: 8,
                        color: theme.palette.secondary.main,
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} pt={1}>
                    <MentionInput
                        ref={mentionInputRef}
                        label='Edit your post...'
                        placeholder='Edit your post...'
                        value={content}
                        onChange={setContent}
                        multiline
                        minRows={2}
                        maxRows={7}
                        fullWidth
                    />

                    {fileError && (
                        <Alert severity='error' onClose={() => setFileError('')}>
                            {fileError}
                        </Alert>
                    )}

                    {/* Existing media */}
                    {showExistingMedia && (
                        <Box
                            sx={{
                                width: '100%',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                position: 'relative',
                            }}
                        >
                            {post.category === PostCategory.MUSIC ? (
                                <AudioPlayer src={existingMediaUrl} />
                            ) : post.category === PostCategory.VIDEO ? (
                                <VideoPlayer src={existingMediaUrl} />
                            ) : (
                                <img
                                    src={existingMediaUrl}
                                    style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                                    alt='Current media'
                                />
                            )}
                            <IconButton
                                onClick={handleRemoveExistingMedia}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    color: theme.palette.primary.main,
                                    backgroundColor: theme.palette.background.paper,
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    )}

                    {/* New file preview */}
                    {newPreviewUrl && fileToUpload && (
                        <Box
                            sx={{
                                width: '100%',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                position: 'relative',
                            }}
                        >
                            {post.category === PostCategory.MUSIC &&
                            fileToUpload.type.startsWith('audio/') ? (
                                <AudioPlayer src={newPreviewUrl} />
                            ) : post.category === PostCategory.VIDEO &&
                              fileToUpload.type.startsWith('video/') ? (
                                <VideoPlayer src={newPreviewUrl} />
                            ) : (
                                <img
                                    src={newPreviewUrl}
                                    style={{ width: '100%', height: 'auto', objectFit: 'cover' }}
                                    alt='New media preview'
                                />
                            )}
                            <IconButton
                                onClick={handleRemoveNewFile}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    color: theme.palette.primary.main,
                                    backgroundColor: theme.palette.background.paper,
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    )}

                    {isUploadingMedia && (
                        <Box>
                            <LinearProgress
                                variant='determinate'
                                value={uploadProgress}
                                sx={{ mb: 1 }}
                            />
                            <Typography variant='body2' align='center'>
                                Uploading: {uploadProgress}%
                            </Typography>
                        </Box>
                    )}

                    <Stack direction='row' justifyContent='space-between'>
                        <Button
                            component='label'
                            variant='contained'
                            startIcon={getUploadIcon()}
                        >
                            {fileToUpload || showExistingMedia ? 'Replace Media' : 'Add Media'}
                            <input
                                type='file'
                                accept={getFileAccept()}
                                hidden
                                onChange={handleFileChange}
                            />
                        </Button>
                        <LoadingButton
                            variant='contained'
                            loading={updateRequest.isLoading()}
                            onClick={handleSubmit}
                        >
                            Save
                        </LoadingButton>
                    </Stack>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default PostEditDialog;
